<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'

import { showModal } from '@/Utils/modalBus'
import { date as formatDate, duration as formatDuration, cleanMediaName } from '@/filtersNew'

import Badge from '@/components/Badge.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInputDuration from '@/components/Atoms/FormInputDuration.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCameraFilled from '@/components/icon/IconCameraFilled.vue'
import IconFilm from '@/components/icon/IconFilm.vue'
import IconBroadcast from '@/components/icon/IconBroadcast.vue'
import IconBreak from '@/components/icon/IconBreak.vue'
import IconHandleVertical from '@/components/icon/IconHandleVertical.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconEdit from '@/components/icon/IconEdit.vue'
import EditDurationModal from '@/components/modals/EditDurationModal.vue'

const store = useStore()

const props = defineProps({
	item: {
		type: Object,
		required: true,
	},
	index: {
		type: Number,
		required: true,
	},
	mediaPulse: {
		type: Object,
		default: null,
	},
})

const playoutConfig = computed(() => store.getters['Playout/playoutConfig'])
const playoutDuration = computed(() => store.getters['Playout/playoutDuration'])
const playoutMaxDurationLimit = computed(() => store.getters['User/playoutMaxDurationLimit'])

const isPlaying = computed(() => {
	return (
		props.item.startTime < moment().valueOf() &&
		moment().valueOf() < props.item.endTime &&
		props.mediaPulse?.alive
	)
})

const sourceIconComputed = computed(() => {
	if (props.item.sourceType === 'live') return IconCameraFilled
	if (props.item.sourceType === 'vod') return IconFilm
	if (props.item.sourceType === 'break') return IconBreak
	return IconFilm
})

// Live source / break names are shown as-is; uploaded video filenames are cleaned up.
const displaySourceName = computed(() => {
	if (props.item.sourceType === 'live' || props.item.sourceType === 'break') {
		return props.item.sourceName
	}
	return cleanMediaName(props.item.sourceName)
})

const startTimeLabel = computed(() => {
	if (!props.item.startTime) return '—'
	return formatDate(props.item.startTime, 'M/DD/YYYY, HH:mm:ss', true)
})

const minTimeComputed = computed(() => {
	return { hours: 0, minutes: 10, seconds: 0 }
})

const durationLabel = computed(() => formatDuration(props.item.endTime - props.item.startTime))

const onDelete = async () => {
	await store.dispatch('Playout/deleteItem', props.item)
}

const onDurationChanged = (value) => {
	// In loop mode the total lineup duration is capped. Block a manual duration
	// change that would push the playout over playoutMaxDurationLimit and warn
	// the user (same modal as when adding an overlong item).
	const isLoopMode = playoutConfig.value?.mode !== 'schedule'
	if (isLoopMode) {
		const newDurationMs = moment.duration(value).asMilliseconds()
		const oldDurationMs = props.item.endTime - props.item.startTime
		const prospectiveTotal = playoutDuration.value - oldDurationMs + newDurationMs
		const durationLimit = (playoutMaxDurationLimit.value || 0) * 60 * 60 * 1000
		if (durationLimit && prospectiveTotal >= durationLimit) {
			showModal('modal-overload-duration')
			return
		}
	}
	store.dispatch('Playout/changeItemDuration', { item: props.item, value })
}

const isScheduleMode = computed(() => playoutConfig.value?.mode === 'schedule')

// Must match the header template in PlayoutScheduleTable.vue.
const gridCols = computed(() =>
	isScheduleMode.value
		? 'grid-cols-[48px_minmax(160px,1fr)_128px_180px_120px_44px]'
		: 'grid-cols-[48px_minmax(160px,1fr)_128px_120px_44px]',
)

const isEditDurationModalVisible = ref(false)
const showEditDurationModal = () => (isEditDurationModalVisible.value = true)
</script>

<template>
	<div
		class="grid gap-x-3 items-center text-tiny text-white px-3 h-12 transition-colors hover:bg-surface-3"
		:class="[
			gridCols,
			{ 'bg-gradient-to-r from-success-transparent to-transparent border-l-2 border-success': isPlaying },
		]"
	>
		<div class="flex items-center text-surface-7">
			<icon-base class="mr-1 cursor-grab text-surface-6">
				<icon-handle-vertical />
			</icon-base>
			{{ index + 1 }}
		</div>
		<div class="flex items-center min-w-0">
			<icon-base v-if="item.sourceType !== 'break'" class="mr-2 shrink-0">
				<component :is="sourceIconComputed" />
			</icon-base>
			<span class="truncate" :title="item.sourceName">{{ displaySourceName }}</span>
			<span v-if="isPlaying" class="ml-2 text-success text-[10px] uppercase tracking-wide shrink-0">
				Playing
			</span>
		</div>
		<div class="flex items-center">
			<Badge v-if="item.sourceType === 'live'" type="error" inline pill size="sm" class="gap-1 shrink-0">
				<icon-base width="12" height="12"><icon-broadcast /></icon-base>
				Live source
			</Badge>
			<Badge v-else-if="item.sourceType !== 'break'" type="main" inline pill size="sm" class="gap-1 shrink-0">
				<icon-base width="12" height="12"><icon-film /></icon-base>
				Video
			</Badge>
		</div>
		<div v-if="isScheduleMode" class="flex items-center text-surface-9 min-w-0">
			<span class="truncate">{{ startTimeLabel }}</span>
		</div>
		<div class="flex items-center justify-end gap-3">
			<div v-if="item.sourceType === 'break'" class="group flex items-center leading-none">
				{{ durationLabel }}
				<FormButton
					isIcon
					size="xs"
					type="link"
					class="opacity-30 group-hover:opacity-100 !text-surface-7 hover:!text-white -mt-1"
					@click="showEditDurationModal"
				>
					<icon-base>
						<icon-edit />
					</icon-base>
				</FormButton>
			</div>
			<FormInputDuration
				v-else-if="item.sourceType === 'live'"
				:minTime="minTimeComputed"
				:minHours="24"
				:id="'duration-v2-' + item._id"
				:modelValue="durationLabel"
				teleport="body"
				@update:modelValue="onDurationChanged"
			/>
			<span v-else class="text-tiny text-surface-9">{{ durationLabel }}</span>
		</div>
		<div class=" flex justify-end">
			<FormButton type="link" isIcon @click="onDelete" class="text-surface-7 hover:text-error-light">
				<icon-base>
					<icon-bin />
				</icon-base>
			</FormButton>
		</div>
		<EditDurationModal
			v-if="item.sourceType === 'break'"
			v-model:is-visible="isEditDurationModalVisible"
			:item="item"
		/>
	</div>
</template>

<style scoped></style>
